import React, { useEffect, useRef, useState } from 'react';
import { useApi } from '../../hooks/useApi';

const SUGGESTIONS = [
  '¿Cuántos casos escalaste hoy y por qué?',
  '¿Quién tiene más carga ahora?',
  '¿Qué integración falló hoy?',
  '¿Cómo nos fue esta semana con los tiempos de respuesta?',
];
const MAX_HISTORY = 10;

/**
 * Talkback (Fase 9.3): el admin le pregunta a la orquestadora sobre la
 * operación. Solo consulta; las respuestas se muestran como texto plano.
 * La conversación vive solo en esta pantalla (no se guarda).
 *
 * @param {string} name  nombre visible de la orquestadora (del catálogo del cliente)
 */
export default function Talkback({ name = 'la IA', compact = false }) {
  const { apiFetch } = useApi();
  const [messages, setMessages] = useState([]);   // { role: 'user'|'assistant'|'error', text }
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView?.({ block: 'nearest' }); }, [messages, busy]);

  async function ask(text) {
    const question = text.trim();
    if (question.length < 3 || busy) return;
    const history = messages.filter(m => m.role !== 'error').slice(-MAX_HISTORY).map(m => ({ role: m.role, text: m.text }));
    setMessages(m => [...m, { role: 'user', text: question }]);
    setDraft('');
    setBusy(true);
    try {
      const data = await apiFetch('/api/admin/talkback', { method: 'POST', body: JSON.stringify({ question, history }) });
      setMessages(m => [...m, { role: 'assistant', text: data.answer }]);
    } catch (e) {
      setMessages(m => [...m, { role: 'error', text: e.message }]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={s.wrap}>
      <div style={s.head}>
        <span style={s.title}>🎙 Pregúntale a {name}</span>
        {messages.length > 0 && <button style={s.clear} onClick={() => setMessages([])} disabled={busy}>Nueva conversación</button>}
      </div>
      <div style={s.hint}>Consulta cómo va la operación. Solo responde preguntas: no reasigna ni cierra casos.</div>

      <div style={{ ...s.list, maxHeight: compact ? 260 : '46vh' }} aria-live="polite">
        {messages.length === 0 && (
          <div style={s.suggestions}>
            {SUGGESTIONS.map(q => <button key={q} style={s.suggestion} onClick={() => ask(q)} disabled={busy}>{q}</button>)}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} style={m.role === 'user' ? s.user : m.role === 'error' ? s.error : s.assistant}>
            {m.role === 'assistant' && <div style={s.who}>{name}</div>}
            {m.role === 'error' ? `⚠️ ${m.text}` : m.text}
          </div>
        ))}
        {busy && <div style={{ ...s.assistant, ...s.thinking }}>{name} está revisando los datos…</div>}
        <div ref={endRef} />
      </div>

      <form style={s.form} onSubmit={e => { e.preventDefault(); ask(draft); }}>
        <input style={s.input} value={draft} onChange={e => setDraft(e.target.value)} maxLength={500}
          placeholder="Escribe tu pregunta…" disabled={busy} aria-label={`Pregunta para ${name}`} />
        <button style={s.send} type="submit" disabled={busy || draft.trim().length < 3}>Preguntar</button>
      </form>
    </div>
  );
}

const bubble = { borderRadius: 12, padding: '8px 12px', fontSize: 13, lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: '92%' };
const s = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 },
  head: { display: 'flex', alignItems: 'center', gap: 8 },
  title: { fontSize: 14, fontWeight: 800, color: 'var(--text)', flex: 1 },
  clear: { background: 'transparent', border: '1px solid var(--border-strong)', borderRadius: 8, padding: '3px 10px', color: 'var(--text-2)', fontSize: 11.5, cursor: 'pointer' },
  hint: { fontSize: 12, color: 'var(--text-3)' },
  list: { display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto', padding: '4px 0' },
  suggestions: { display: 'flex', flexDirection: 'column', gap: 6 },
  suggestion: { textAlign: 'left', background: 'var(--accent-soft)', border: '1px solid var(--accent-border)', borderRadius: 10, padding: '7px 11px', color: 'var(--accent-text)', fontSize: 12.5, cursor: 'pointer' },
  user: { ...bubble, alignSelf: 'flex-end', background: 'var(--accent-soft)', color: 'var(--text)' },
  assistant: { ...bubble, alignSelf: 'flex-start', background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' },
  who: { fontSize: 11, fontWeight: 700, color: 'var(--accent-text)', marginBottom: 2 },
  thinking: { color: 'var(--text-2)', fontStyle: 'italic' },
  error: { ...bubble, alignSelf: 'flex-start', background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', color: 'var(--danger)' },
  form: { display: 'flex', gap: 8 },
  input: { flex: 1, minWidth: 0, background: 'var(--surface-2)', border: '1px solid var(--border-strong)', borderRadius: 10, padding: '8px 12px', color: 'var(--text)', fontSize: 13 },
  send: { background: 'var(--accent)', border: 'none', borderRadius: 10, padding: '8px 14px', color: 'var(--on-accent)', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
};
